import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ToastContainer } from 'react-toastify';
import LinkAccountBG from '../assets/images/link-account-bg.png';
import { showToast } from '../helper/ShowToast';
import { useDebounce } from '../hooks/useDebounce';
import { useSearchArtist, useAssignArtistToUser } from '../queries/artist.query';
import { useRequestInvite } from '../queries/invite.query';
import { loginAuth, fetchAccessContext } from '../redux/actions/auth';
import { maybeSyncWorkspaceOnce } from '../redux/actions/billing';

const COPY = {
    case1Primary: "No worries if you don’t have enough data yet or don’t have a Spotify profile.",
    case1Supporting: "Enter UpNComer to see what you can already do to grow and progress as an artist.",
    case2Primary: "You’re all set to become an UpNComer.",
    case2Supporting: "Let’s get you inside and start building.",
    enterCta: "Enter UpNComer",
    skipHint: "Don’t have Spotify or can’t find your profile? You can still enter and explore the platform.",
};

export const LinkAccounts = () => {
    const location = useLocation();
    const data = location.state || {};
    const registrationUser = data.user || {};
    const email = registrationUser.email || data.email || "";
    const userType = registrationUser.userType || data.userType || "user";
    const token = data.token || registrationUser.token;
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [searchTerm, setSearchTerm] = useState("");
    const [selectedArtist, setSelectedArtist] = useState(null);
    const [showDropdown, setShowDropdown] = useState(false);
    const [showNotFoundMessage, setShowNotFoundMessage] = useState(false);
    const [distributionEmail, setDistributionEmail] = useState(email || '');
    const [isEntering, setIsEntering] = useState(false);

    const dropdownRef = useRef(null);

    // debounce term
    const debouncedSearch = useDebounce(searchTerm, 500);

    // React Query Search
    const {
        data: searchResults = [],
        isLoading: isSearching,
    } = useSearchArtist({
        name: debouncedSearch,
        enabled: !!debouncedSearch?.trim() && !selectedArtist,
    });

    // React Query Mutation for Add Artist
    const { mutate: assignArtist, isPending: isAssigning } = useAssignArtistToUser({
        navigate,
    });
    // query mutation for request invite
    const { mutate: requestInvite, isPending: isRequestPending } = useRequestInvite();

    // handle outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // show dropdown when typing
    useEffect(() => {
        if (debouncedSearch?.trim() && !selectedArtist) {
            setShowDropdown(true);
        }
    }, [debouncedSearch, selectedArtist]);

    // Artist not found message
    useEffect(() => {
        if (
            debouncedSearch?.trim() &&
            !isSearching &&
            !selectedArtist &&
            searchResults?.length === 0
        ) {
            setShowNotFoundMessage(true);
        } else {
            setShowNotFoundMessage(false);
        }
    }, [debouncedSearch, isSearching, selectedArtist, searchResults]);

    const handleArtistSelect = (artist) => {
        setSelectedArtist(artist);
        setSearchTerm(artist?.name || "");
        setShowDropdown(false);
        setShowNotFoundMessage(false);
    };


    const handlePrimaryCta = (e) => {
        e.preventDefault();

        if (selectedArtist) {
            assignArtist({
                songstats_artist_id: selectedArtist.songstats_artist_id,
            });
            return;
        }

        handleEnterUpNComer();
    };

    const handleCloseNotFound = () => {
        setShowNotFoundMessage(false);
        setSearchTerm("");
        setSelectedArtist(null);
        setShowDropdown(false);
    };

    const validate = () => {
        if (!distributionEmail.trim()) return "Email is required.";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(distributionEmail.trim()))
            return "Please enter a valid email address.";
        return null;
    };

    const handleRequestSubmit = () => {
        const validationError = validate();
        if (validationError) {
            showToast("warning", validationError);
            return;
        }
        requestInvite(
            {
                email: distributionEmail.trim(),
                accountType: userType === 'organization' ? "Label" : "Artist"
            },
            {
                onSuccess: () => {
                    showToast(
                        "success",
                        "Request submitted successfully!"
                    );
                    setDistributionEmail("");
                },
                onError: (error) => {
                    showToast(
                        "error",
                        error?.response?.data?.error ||
                        "Failed to submit request"
                    );
                },
            }
        );
    };

    const handleEnterUpNComer = async () => {
        setIsEntering(true);
        try {
            const existingToken = token || localStorage.getItem("authToken");
            if (existingToken) {
                localStorage.setItem("authToken", existingToken);
            }

            let session = null;
            try {
                session = await dispatch(fetchAccessContext());
            } catch (error) {
                // Non-blocking: user should still enter platform.
            }

            const { user: profileFromApi, credits, ...accessPayload } = session || {};
            const userForAuth = profileFromApi || {
                ...registrationUser,
                email,
                userType,
                assignedArtistSongstateId:
                    registrationUser.assignedArtistSongstateId ?? "",
            };
            const accessForStore = session
                ? { ...accessPayload, credits: credits ?? null }
                : null;

            dispatch(loginAuth(existingToken || "", userForAuth, accessForStore));
            // TODO(legacy-migration): Phase 2 — remove maybeSyncWorkspaceOnce after backend login sync is removed.
            dispatch(maybeSyncWorkspaceOnce());
            navigate('/dashboard', { replace: true });
        } finally {
            setIsEntering(false);
        }
    };

    return (
        <div className="auth-page min-h-screen flex items-center justify-center">
            <ToastContainer />

            <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen w-full">
                {/* LEFT */}
                <div className="grid-item">
                    <div className="left-sec bg-theme_clr1 h-full overflow-y-auto px-5 py-10 lg:p-[50px] 2xl:p-[69px]">
                        <div className="w-full max-w-[625px] mx-auto">

                            {/* FORM */}
                            <form
                                onSubmit={handlePrimaryCta}
                                className="font-manrope w-full"
                            >
                                {/* Logo */}
                                <div
                                    className="logo-wrapper mb-6 md:mb-7 xl:mb-[33px] cursor-pointer text-center"
                                    onClick={() => navigate("/")}
                                >
                                    <img
                                        src={require("../assets/images/logo.png")}
                                        alt="Logo"
                                        className="logo max-w-[145px] 2xl:max-w-[200px] mx-auto"
                                    />
                                    <div className="line-bar bg-[#EDECF2] opacity-[15%] h-[1px] w-[64px] mx-auto"></div>
                                </div>

                                {/* Heading */}
                                <div className="heading text-white text-[25px] lg:text-[32px] 2xl:text-[54px] font-medium mb-[21px] leading-tight text-center">
                                    It’s time to take control.
                                </div>

                                <div className="text-white xl:text-xl font-manrope text-center">
                                    Set up your account and take control of your career with UpNComer.
                                </div>

                                {/* Input */}
                                <div className="input-cover text-left mt-10 2xl:mt-14 mb-4">
                                    <div className="label text-white xl:text-xl font-manrope mb-5 xl:mb-8">
                                        Enter your Artist Name or Spotify URL
                                    </div>

                                    <div className="relative" ref={dropdownRef}>
                                        <div className="flex items-center justify-between gap-[20px] xl:gap-[35px]">
                                            <span>
                                                <img
                                                    src={require("../assets/images/icons/spotify-icon.png")}
                                                    alt="Spotify"
                                                    className="block min-w-[49px] w-[49px] h-[49px]"
                                                />
                                            </span>

                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Artist Name or Spotify URL"
                                                autoComplete="off"
                                                value={searchTerm}
                                                onChange={(e) => {
                                                    setSelectedArtist(null);
                                                    setSearchTerm(e.target.value);
                                                    setShowDropdown(true);
                                                }}
                                                onFocus={() => {
                                                    if (searchResults?.length > 0) {
                                                        setShowDropdown(true);
                                                    }
                                                }}
                                            />
                                        </div>

                                        {/* Dropdown */}
                                        {showDropdown &&
                                            searchTerm.trim() &&
                                            (isSearching || searchResults?.length > 0) && (
                                                <div
                                                    className="search-results w-full bg-white rounded-lg shadow-lg overflow-auto mt-2 p-2"
                                                    style={{ maxHeight: "250px" }}
                                                >
                                                    {/* Loading */}
                                                    {isSearching && (
                                                        <div className="px-3 py-2 text-sm text-gray-600">
                                                            Searching...
                                                        </div>
                                                    )}

                                                    {/* Results */}
                                                    {!isSearching &&
                                                        searchResults?.length > 0 && (
                                                            <ul className="list-none">
                                                                {searchResults.map((artist) => (
                                                                    <li
                                                                        key={artist.songstats_artist_id}
                                                                        className="cursor-pointer flex items-center gap-3 rounded-md px-3 py-2 hover:bg-gray-100 transition"
                                                                        onClick={() =>
                                                                            handleArtistSelect(artist)
                                                                        }
                                                                    >
                                                                        <img
                                                                            src={
                                                                                artist.avatar ||
                                                                                "default-avatar-url.png"
                                                                            }
                                                                            alt={artist.name}
                                                                            className="w-[35px] h-[35px] rounded-full object-cover"
                                                                        />

                                                                        <span className="text-gray-800 capitalize text-base font-bold">
                                                                            {artist.name}
                                                                        </span>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        )}
                                                </div>
                                            )}
                                    </div>

                                    {/* Artist Not Found Message */}
                                    {showNotFoundMessage && (
                                        <div className="mt-4 relative rounded-xl border border-blue-200 bg-blue-50 shadow-md p-4 pr-12">

                                            {/* Close Button */}
                                            <button
                                                type="button"
                                                onClick={handleCloseNotFound}
                                                className="absolute top-3 right-3 text-slate-500 hover:text-slate-800 transition"
                                            >
                                                ✕
                                            </button>

                                            {/* Content */}
                                            <div className="flex items-start gap-3">
                                                <div className="min-w-[38px] h-[38px] rounded-full bg-blue-100 flex items-center justify-center text-lg">
                                                    ℹ️
                                                </div>

                                                <div>
                                                    <p className="text-slate-900 font-semibold text-base mb-1">
                                                        {COPY.case1Primary}
                                                    </p>

                                                    <p className="text-slate-700 text-sm leading-relaxed mb-4">
                                                        {COPY.case1Supporting}
                                                    </p>

                                                    <button
                                                        type="button"
                                                        onClick={handleEnterUpNComer}
                                                        disabled={isAssigning || isEntering}
                                                        className="rounded-lg bg-theme_clr2 text-white px-4 py-2 text-sm font-medium hover:opacity-90 transition disabled:opacity-70 disabled:cursor-not-allowed"
                                                    >
                                                        {isEntering ? (
                                                            <span className="loader"></span>
                                                        ) : (
                                                            COPY.enterCta
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {selectedArtist && (
                                    <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-left">
                                        <p className="text-slate-900 font-semibold text-base mb-1">
                                            {COPY.case2Primary}
                                        </p>
                                        <p className="text-slate-700 text-sm leading-relaxed">
                                            {COPY.case2Supporting}
                                        </p>
                                    </div>
                                )}

                                {/* Main CTA hidden while not-found panel is shown (CTA lives in that panel) */}
                                {!showNotFoundMessage && (
                                    <div className="btn-wrapper mt-6 md:mt-10 2xl:mt-14">
                                        <button
                                            type="submit"
                                            className={`loader-button w-full font-medium flex items-center justify-center gap-[4.5px] rounded-full px-5 py-[11.5px] transition ${
                                                selectedArtist
                                                    ? "bg-theme_clr2 text-white"
                                                    : "bg-white text-theme_clr1 border border-theme_clr1"
                                            }`}
                                            disabled={isAssigning || isEntering}
                                        >
                                            {isAssigning || isEntering ? (
                                                <span className="loader"></span>
                                            ) : (
                                                COPY.enterCta
                                            )}
                                        </button>
                                        {!selectedArtist && (
                                            <p className="text-white/80 text-sm text-center mt-4 px-2">
                                                {COPY.skipHint}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </form>

                            {/* Distribution Access Section */}
                            <div className="mt-8">

                                {/* Divider Line */}
                                <div className="w-full h-[1px] bg-white/20 mb-8"></div>

                                {/* Top Heading Outside Card */}
                                <h3 className="text-white text-[16px] md:text-[19px] font-normal leading-relaxed text-center mb-5 max-w-[560px] mx-auto">
                                    Haven't distributed your music before, or just looking for a distributor that actually cares about your growth?
                                </h3>

                                {/* White Card */}
                                <div className="rounded-[24px] bg-white shadow-xl border border-gray-200 p-6 md:p-8 text-center relative">

                                    {/* Beta Badge - Top Left */}
                                    <div className="absolute top-6 left-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-100 border border-gray-200 text-xs font-semibold tracking-wide text-gray-500">
                                        <span className="w-2.5 h-2.5 rounded-full bg-green-500"></span>
                                        BETA ACCESS
                                    </div>

                                    {/* Description */}
                                    <p className="text-gray-600 text-sm md:text-base leading-relaxed max-w-[520px] mx-auto mt-12 mb-8">
                                        Request access to UpNComer’s Distribution Portal to start releasing, grow your catalog, and unlock everything the platform has to offer.
                                    </p>

                                    {/* Email Label */}
                                    <div className="text-left mb-2 text-xs font-semibold tracking-[1px] text-gray-500 uppercase">
                                        Artist Email
                                    </div>

                                    {/* Email Input */}
                                    <input
                                        type="email"
                                        placeholder="you@example.com"
                                        value={distributionEmail}
                                        onChange={(e) => setDistributionEmail(e.target.value)}
                                        className="w-full rounded-xl border border-gray-300 px-5 py-4 text-gray-800 placeholder:text-gray-400 outline-none focus:ring-2 focus:ring-theme_clr1 focus:border-theme_clr1 transition"
                                    />

                                    {/* CTA Button */}
                                    <button
                                        type="button"
                                        onClick={handleRequestSubmit}
                                        disabled={isRequestPending}
                                        className="w-full mt-6 rounded-xl bg-theme_clr2 text-white font-medium px-5 py-4 shadow-md hover:shadow-lg hover:scale-[1.01] transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100"
                                    >
                                        {isRequestPending ? (
                                            <span className="flex items-center justify-center gap-2">
                                                <span className="loader"></span>
                                            </span>
                                        ) : (
                                            "Request Distribution Access"
                                        )}
                                    </button>

                                    {/* Bottom Note */}
                                    <p className="text-sm text-gray-500 mt-5 leading-relaxed">
                                        We will send you an invite to join UpNComer's Distribution Portal on this email.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* RIGHT */}
                <div className="grid-item hidden lg:block">
                    <div
                        className="right-sec h-full min-h-screen flex items-center justify-center bg-cover bg-no-repeat bg-left px-5 py-12 lg:p-[50px] 2xl:p-[69px]"
                        style={{ backgroundImage: `url(${LinkAccountBG})` }}
                    ></div>
                </div>
            </div>
        </div>
    );
};